/**
 * Money arrives from the API in micros — one currency unit is 1,000,000 — so a
 * sub-cent hourly rate survives the trip. This is the only place it becomes
 * something a person reads.
 */

const MICROS_PER_UNIT = 1_000_000;

/**
 * The divisor the API bills with, mirrored from `lib/money.ts` there.
 *
 * 28 × 24 = 672, the shortest month, so a full month always costs the
 * advertised price and continuous running pays it off after 28 days. It was
 * 730 until the catalogue was re-priced from a rate card computed at 672.
 *
 * Exported because three pages derived it independently and two of them wrote
 * the number inline — which is how a console ends up quoting a rate the
 * platform stopped charging. If this ever disagrees with the API, the API is
 * right and this is a display bug.
 */
export const HOURS_PER_MONTH = 672;

/** The hourly rate for a monthly price, floored exactly as the server floors. */
export function hourlyMicros(priceCents: number): number {
  return Math.floor((priceCents * 10_000) / HOURS_PER_MONTH);
}

export function money(micros: number, currency = "USD"): string {
  return format(micros / MICROS_PER_UNIT, currency, 2, 2);
}

/**
 * Compact form for tiles, where the exact cents are noise.
 *
 * An operator reading a liability figure wants the order of magnitude at a
 * glance; the precise number is on the row below it.
 */
export function moneyCompact(micros: number, currency = "USD"): string {
  const units = Math.abs(micros) / MICROS_PER_UNIT;
  if (units >= 1_000_000) return `${sign(micros)}${symbol(currency)}${(units / 1_000_000).toFixed(1)}M`;
  if (units >= 1_000) return `${sign(micros)}${symbol(currency)}${(units / 1_000).toFixed(1)}k`;
  return money(micros, currency);
}

/**
 * A rate, which may be a fraction of a cent. A $5/month tier is $0.007440 an
 * hour; rounding that to two places would overstate it by nearly half.
 */
export function rate(micros: number, currency = "USD"): string {
  const units = micros / MICROS_PER_UNIT;
  return units !== 0 && Math.abs(units) < 0.01
    ? format(units, currency, 4, 4)
    : format(units, currency, 2, 2);
}

function sign(micros: number): string {
  return micros < 0 ? "−" : "";
}

function symbol(currency: string): string {
  try {
    // Extract whatever glyph the locale uses, rather than assuming "$".
    return (
      new Intl.NumberFormat(undefined, { style: "currency", currency })
        .formatToParts(0)
        .find((part) => part.type === "currency")?.value ?? `${currency} `
    );
  } catch {
    return `${currency} `;
  }
}

function format(value: number, currency: string, min: number, max: number): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    }).format(value);
  } catch {
    // An operator can configure any three-letter currency, including nonsense.
    return `${value.toFixed(max)} ${currency}`;
  }
}

/**
 * What a money field may contain, or null to refuse the keystroke.
 *
 * These forms use `type="number"`, which has two problems on an amount. The
 * visible one is the spinner arrows; the one that costs money is that a scroll
 * over a focused field silently changes the value — and one of these fields
 * moves a customer's balance directly.
 *
 * A whitelist rather than a parse, because `Number()` accepts a great deal that
 * is not an amount: `1e9` is a hundred million, `abc` is `NaN`. Refusing the
 * keystroke keeps the field and whatever reads it from ever disagreeing.
 *
 * Two decimals, because these are submitted as whole cents and a third would be
 * rounded away in silence after somebody typed it.
 *
 * `negative` is for the credit adjustment alone, where a minus is the whole
 * point — it is how an operator takes credit back.
 */
export function acceptAmount(next: string, opts: { negative?: boolean } = {}): string | null {
  const pattern = opts.negative ? /^-?\d*\.?\d{0,2}$/ : /^\d*\.?\d{0,2}$/;
  return pattern.test(next) ? next : null;
}

/** The same, for a field that counts things: days, redemptions, a percentage. */
export function acceptWhole(next: string): string | null {
  return /^\d*$/.test(next) ? next : null;
}

/**
 * An amount in the currency a customer was actually charged in, as the
 * processor's statement prints it. Rupiah has no minor unit in use and its
 * own grouping ("Rp165.000"); anything else falls back to the generic
 * two-decimal form. Whole units in, never micros -- the charged side of a
 * payment is stored that way.
 */
export function chargedMoney(amount: number, currency: string): string {
  if (currency === "IDR") return `Rp${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(amount)}`;
  return format(amount, currency, 2, 2);
}
